<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { PhxWatchComparator } from '@phx-styleguides/jlc';
import { clientApi } from '@phoenix/clientApi';
import { emitter } from '@phoenix/event-bus';
import { WATCH_COMPARATOR_UPDATED } from '@maison/helpers/watch-comparator-localstorage-helper';
import { useWatchComparator } from '@maison/composables/watchComparator';

const props = defineProps({
  panelId: {
    type: String,
    required: true,
  },
  groups: {
    type: Array,
    default: () => [],
  },
  intro: {
    type: String,
    default: '',
  },
  helpUrl: {
    type: String,
    default: '',
  },
  seeAllUrl: {
    type: String,
    default: '',
  },
  i18n: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const { watches, remove } = useWatchComparator();

const products = ref([]);
let loadedReferences = null;
let panelEl = null;

const productsWithWishlist = computed(() =>
  products.value.map((product) => ({
    ...product,
    inWishlist: store.getters['wishlist/isInWishlist'](product.reference),
  })),
);

const hasGroupValues = (product, group) => {
  const spec = product.specs?.[group.key];

  if (!spec) {
    return false;
  }

  return group.categories ? group.categories.some((category) => spec[category.key]?.length > 0) : spec.length > 0;
};

const groupsWithProperties = computed(() =>
  props.groups.filter((group) => products.value.some((product) => hasGroupValues(product, group))),
);

const getSelectedReferences = () => watches.value.map((watch) => watch.reference);

const isPanelOpen = () => panelEl?.classList.contains('is-active') ?? false;

const loadProducts = async () => {
  const references = getSelectedReferences();
  const key = references.join(',');

  if (key === loadedReferences) {
    return;
  }

  if (!references.length) {
    products.value = [];
    loadedReferences = key;
    return;
  }

  try {
    const { data } = await clientApi.get(window.route('api.watchComparator.products'), {
      params: { references },
    });
    products.value = data.data?.products ?? [];
    loadedReferences = key;
  } catch {
    products.value = [];
    loadedReferences = null;
  }
};

const removeProduct = ({ product }) => {
  if (!product?.reference) {
    return;
  }

  products.value = products.value.filter((item) => item.reference !== product.reference);
  loadedReferences = getSelectedReferences()
    .filter((reference) => reference !== product.reference)
    .join(',');
  remove(product.reference);
};

const toggleWishlist = ({ product }) => {
  if (product?.reference) {
    store.dispatch('wishlist/toggleProduct', { reference: product.reference, showBanner: false });
  }
};

const goToProduct = ({ product }) => {
  if (product.href) {
    window.location.assign(product.href);
  }
};

const goToAllWatches = () => {
  if (props.seeAllUrl) {
    window.location.assign(props.seeAllUrl);
  }
};

const onPanelShown = () => loadProducts();

const onUpdate = () => {
  if (isPanelOpen()) {
    loadProducts();
  }
};

onMounted(() => {
  panelEl = document.getElementById(props.panelId);
  panelEl?.addEventListener('panelshown', onPanelShown);
  emitter.on(WATCH_COMPARATOR_UPDATED, onUpdate);

  if (isPanelOpen()) {
    loadProducts();
  }
});

onBeforeUnmount(() => {
  panelEl?.removeEventListener('panelshown', onPanelShown);
  emitter.off(WATCH_COMPARATOR_UPDATED, onUpdate);
});
</script>

<template>
  <phx-watch-comparator
    :id="panelId"
    :products="productsWithWishlist"
    :groups="groupsWithProperties"
    :intro="intro"
    :help-cta="{ href: helpUrl }"
    :see-all-cta="{ href: seeAllUrl }"
    :i18n="i18n"
    @remove="removeProduct"
    @wishlist="toggleWishlist"
    @add-to-cart="goToProduct"
    @add="goToAllWatches"
  />
</template>
